import { createServerFn } from "@tanstack/react-start"
import { groupBy } from "lodash-es"

export const SPEND_WINDOW_DAYS = 30

// Every Monitoring Run started inside the window with its summed usage. The
// page buckets Runs into days in the viewer's time zone, so a Run's spend lands
// on the day it started.
export const loadSpend = createServerFn({ method: "GET" }).handler(async () => {
  const [
    { assertAdminContext },
    { desc, gte, inArray, sql },
    { terminalCompanyRun, terminalMonitoringRun },
    { db },
    { monitoringRunState },
    { readCompanyRunUsage, sumPipelineUsage },
  ] = await Promise.all([
    import("@/lib/supabase/auth"),
    import("drizzle-orm"),
    import("@/db/schema/terminal-schema"),
    import("@/lib/admin-db"),
    import("@/terminal/monitoring/monitoring-operations"),
    import("@/terminal/monitoring/monitoring-usage"),
  ])

  // A server function is directly callable, so the route guard is not enough.
  await assertAdminContext()

  return db.transaction(
    async (tx) => {
      // A day of margin so the earliest local day is whole in any time zone.
      const windowStart = new Date(
        Date.now() - (SPEND_WINDOW_DAYS + 1) * 86_400_000
      ).toISOString()
      const runs = await tx
        .select({
          monitoringRunId: terminalMonitoringRun.id,
          startedAt: terminalMonitoringRun.started_at,
          finishedAt: terminalMonitoringRun.finished_at,
          abandonedAt: terminalMonitoringRun.abandoned_at,
          plannedCompanyCount: sql<number>`cardinality(${terminalMonitoringRun.planned_company_ids})`,
        })
        .from(terminalMonitoringRun)
        .where(gte(terminalMonitoringRun.started_at, windowStart))
        .orderBy(desc(terminalMonitoringRun.started_at))
      const companyRuns = runs.length
        ? await tx
            .select({
              id: terminalCompanyRun.id,
              monitoringRunId: terminalCompanyRun.monitoring_run_id,
            })
            .from(terminalCompanyRun)
            .where(
              inArray(
                terminalCompanyRun.monitoring_run_id,
                runs.map((run) => run.monitoringRunId)
              )
            )
        : []
      const usageByCompanyRunId = await readCompanyRunUsage(
        tx,
        companyRuns.map((row) => row.id)
      )
      const companyRunsByRunId = groupBy(companyRuns, "monitoringRunId")
      return {
        refreshedAt: new Date().toISOString(),
        runs: runs.map(({ finishedAt, abandonedAt, ...run }) => ({
          ...run,
          state: monitoringRunState({ finishedAt, abandonedAt }),
          usage: sumPipelineUsage(
            (companyRunsByRunId[run.monitoringRunId] ?? []).map(
              (row) => usageByCompanyRunId.get(row.id)!
            )
          ),
        })),
      }
    },
    { accessMode: "read only", isolationLevel: "repeatable read" }
  )
})

export type SpendData = Awaited<ReturnType<typeof loadSpend>>

export const loadProviderAccounts = createServerFn({ method: "GET" }).handler(
  async () => {
    const [{ assertAdminContext }, { readProviderAccounts }] =
      await Promise.all([
        import("@/lib/supabase/auth"),
        import("@/admin/spend/provider-accounts"),
      ])
    await assertAdminContext()
    return readProviderAccounts()
  }
)

export type ProviderAccounts = Awaited<ReturnType<typeof loadProviderAccounts>>
